'use client';
/**
 * 动态获取商品数据 (使用 SWR)
 * 用于在组件挂载时获取商品数据，支持预览模式和正常模式，并利用 SWR 实现数据缓存与滚动位置保留
 */

import { useStore } from '@/providers/app-store-provider';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { serializeKey } from '@/utils/swr/keys';
import { useEffect, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';

interface UseFetchDynamicProductsParams {
  componentId?: string;
  componentBlockId?: string | number;
  currencyName?: string;
  enableAuthRefetch?: boolean;
  inView?: boolean;
  hasProducts?: boolean;
}

export function useFetchDynamicProducts<T>({
  componentId,
  componentBlockId,
  currencyName,
  enableAuthRefetch = true,
  inView = true,
  hasProducts = false,
}: UseFetchDynamicProductsParams) {
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const isLoggedIn = !!token;

  const isPreview =
    typeof window !== 'undefined' && window.location.pathname.includes(HOME_PREVIEW_PATH);
  const status = isPreview ? 2 : 1;

  // 将 Key 设计为全局 Fetcher 支持的 [URL, Params] 格式
  // 我们使用 params 字符串化并排序的方式，保证 Key 序列化后的绝对稳定
  const requestParams = {
    data: {
      component_id: componentId!,
      id: Number(componentBlockId),
      currency: currencyName || 'USD',
      status,
    },
  };

  const swrKeyString = serializeKey(['POST:/api/block/get_product_data', requestParams]);

  // 1. 获取全局配置中的 cache
  const { cache } = useSWRConfig();

  // 2. 为了提前判断 hasCache，我们用自己算出来的、将要传给 SWR 的 swrKeyString 去内存里探测
  const cachedState = swrKeyString ? cache.get(swrKeyString) : undefined;
  const hasCache = cachedState && cachedState.data !== undefined;

  // 3. 只有在 (可见 inView 或者 已有缓存 hasCache)、没有预设静态商品数据，且组件ID有效时，才发起请求
  const shouldFetch = (inView || hasCache) && !hasProducts && !!componentId && !!componentBlockId;

  // 4. 最终传递给 SWR 的 Key (挂起请求时传 null)
  const swrKey = shouldFetch ? swrKeyString : null;

  const {
    data: dynamicProductsData,
    error,
    isLoading,
    mutate,
  } = useSWR<T | null>(swrKey, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    dedupingInterval: 300000,
    keepPreviousData: true,
  });

  const authStatusRef = useRef(isLoggedIn);
  useEffect(() => {
    if (!clientTokenReady) return;
    if (authStatusRef.current === isLoggedIn) {
      return;
    }
    authStatusRef.current = isLoggedIn;

    if (enableAuthRefetch) {
      mutate();
    }
  }, [isLoggedIn, enableAuthRefetch, mutate, clientTokenReady]);

  return {
    dynamicProductsData: dynamicProductsData || null,
    isLoading,
    hasAttemptedFetch: dynamicProductsData !== undefined || !!error,
  };
}
